import {
  clone,
  concat,
  each,
  filter,
  find,
  get,
  groupBy,
  indexOf,
  map,
  omit,
  pick,
  reduce,
  set,
  sortBy,
  uniq,
  uniqBy,
} from 'lodash';

import type {
  GetOrgYouthsResponse,
  GetRecipientsResponse,
} from '@appTypes/esb';
import type { Recipient } from '@appTypes/esb/organizations/v2/_orgGuid/recipients';
import { YouthParentPositions, personTagType } from '@shared/constants';

const getSubunits = (recipients: Recipient[]) =>
  sortBy(
    uniq(
      reduce(
        recipients,
        (memo: string[], recipient: Recipient) => {
          each(recipient.subunits, (subunit: string) => memo.push(subunit));
          return memo;
        },
        [],
      ),
    ),
    item => item,
  );

const sortRecipients = (recipients: Recipient[]) =>
  sortBy(recipients, ['lastName', 'firstName']);

const addDropFilter = (recipient: Recipient, filters: string[]) => {
  if (recipient.noEmails || recipient.optedOut) {
    return [...filters, 'dropping'];
  }
  return filters;
};

const getInactiveReason = (recipient: Recipient) => {
  if (!get(recipient, 'hasEmail', false)) {
    return 'NO_EMAIL';
  }

  if (get(recipient, 'noEmails', false)) {
    return 'NO_EMAILS_OPTION';
  }

  if (get(recipient, 'optedOut', false)) {
    return 'OPT_OUT';
  }

  return 'ACTIVE';
};

const transformRecipient = (
  recipient: Recipient,
  filters: string[],
  tags: string[],
) => ({
  ...pick(recipient, ['memberId', 'userId', 'firstName', 'lastName', 'gender']),
  active:
    get(recipient, 'hasEmail', false) &&
    !get(recipient, 'noEmails', false) &&
    !get(recipient, 'optedOut', false),
  inactiveReason: getInactiveReason(recipient),
  relationships: [],
  memberIdsToAdd: [],
  memberIdsToRemove: [],
  filters: addDropFilter(recipient, filters),
  subunits: uniq(get(recipient, 'subunits', [])),
  tags,
});

export default (
  youthData: GetOrgYouthsResponse | undefined,
  recipientsList: GetRecipientsResponse,
) => {
  try {
    const youthPositions = groupBy(get(youthData, 'members', []), 'memberId');
    const youthList = {};
    let recipients = reduce(
      get(recipientsList, 'parents', []),
      (memo, parent) => {
        const memberId = get(parent, 'memberId');
        set(
          memo,
          memberId,
          transformRecipient(
            parent,
            ['all', 'parent'],
            [personTagType.PARENT_GUARDIAN],
          ),
        );
        return memo;
      },
      {},
    );
    recipients = reduce(
      get(recipientsList, 'leaders', []),
      (memo, leader) => {
        const memberId = get(leader, 'memberId');
        if (!get(memo, memberId)) {
          set(memo, memberId, transformRecipient(leader, ['all'], []));
        }
        set(memo, `[${memberId}].tags`, [
          personTagType.LEADER,
          ...get(memo, `[${memberId}].tags`, []),
        ]);
        set(memo, `[${memberId}].filters`, [
          'leader',
          ...get(memo, `[${memberId}].filters`, []),
        ]);

        const noEmailsCurrentValue = !!get(memo, `[${memberId}].noEmails`);
        const noEmailsLeaderValue = !!get(leader, 'noEmails');
        if (noEmailsCurrentValue !== noEmailsLeaderValue) {
          set(memo, `[${memberId}].noEmails`, true);
        }
        return memo;
      },
      recipients,
    );
    recipients = reduce(
      get(recipientsList, 'participants', []),
      (memo, participant) => {
        const memberId = get(participant, 'memberId');
        if (!get(memo, memberId)) {
          set(memo, memberId, transformRecipient(participant, ['all'], []));
        }
        set(memo, `[${memberId}].tags`, [
          personTagType.PARTICIPANT,
          ...get(memo, `[${memberId}].tags`, []),
        ]);
        set(memo, `[${memberId}].filters`, [
          'participant',
          ...get(memo, `[${memberId}].filters`, []),
        ]);

        const noEmailsCurrentValue = !!get(memo, `[${memberId}].noEmails`);
        const noEmailsLeaderValue = !!get(participant, 'noEmails');
        if (noEmailsCurrentValue !== noEmailsLeaderValue) {
          set(memo, `[${memberId}].noEmails`, true);
        }
        return memo;
      },
      recipients,
    );
    recipients = reduce(
      get(recipientsList, 'youths', []),
      (memo, youth) => {
        const recipient: Recipient = transformRecipient(
          youth,
          ['all', 'youth'],
          [personTagType.YOUTH],
        );

        let shouldBeFiltered = false;
        each(
          get(youthPositions, get(youth, 'memberId', 0).toString(), []),
          item => {
            const parent = find(
              get(recipientsList, 'parents', []),
              match => match.memberId === youth.memberId,
            );
            const leader = find(
              get(recipientsList, 'leaders', []),
              match => match.memberId === youth.memberId,
            );

            if (parent || leader) {
              if (indexOf(YouthParentPositions, item.position) !== -1) {
                shouldBeFiltered = true;
              }
            }
          },
        );

        if (!shouldBeFiltered) {
          const relationships = get(youth, 'relationships', []);
          if (relationships.length) {
            each(relationships, relationship => {
              const memberId = get(relationship, 'memberId');
              const parent =
                get(memo, memberId) ||
                transformRecipient(
                  relationship,
                  ['all', 'parent'],
                  [personTagType.PARENT_GUARDIAN],
                );
              if (!get(memo, memberId)) {
                set(memo, memberId, parent);
              }
              set(
                recipient,
                'relationships',
                uniqBy(
                  [
                    omit(parent, ['relationships']),
                    ...get(recipient, 'relationships', []),
                  ],
                  'memberId',
                ),
              );
              if (get(parent, 'active', false)) {
                set(
                  recipient,
                  'memberIdsToAdd',
                  uniq([
                    get(parent, 'memberId'),
                    ...get(recipient, 'memberIdsToAdd', []),
                  ]),
                );
              }
              const newRecipient = clone(recipient);
              if (!get(memo, `[${memberId}].active`, false)) {
                set(newRecipient, 'active', false);
                set(newRecipient, 'inactiveReason', 'NO_ACTIVE_GUARDIAN');
              }
              set(
                memo,
                `[${memberId}].relationships`,
                sortRecipients(
                  uniqBy(
                    [
                      newRecipient,
                      ...get(memo, `[${memberId}].relationships`, []),
                    ],
                    'memberId',
                  ),
                ),
              );
              set(
                memo,
                `[${memberId}].memberIdsToRemove`,
                uniq([
                  get(recipient, 'memberId'),
                  ...get(memo, `[${memberId}].memberIdsToRemove`, []),
                ]),
              );
            });
          } else {
            if (!get(memo, -1)) {
              set(memo, -1, {
                firstName: '-',
                lastName: '-',
                active: false,
                inactiveReason: 'NO_EMAIL',
                tags: [personTagType.PARENT_GUARDIAN],
                filters: ['all', 'parent'],
                relationships: [],
                memberIdsToAdd: [],
                memberIdsToRemove: [],
                isInvalid: true,
              });
            }
            set(
              memo,
              '[-1].relationships',
              sortRecipients(
                uniqBy(
                  [recipient, ...get(memo, '[-1].relationships', [])],
                  'memberId',
                ),
              ),
            );
            set(
              memo,
              '[-1].memberIdsToRemove',
              uniq([
                get(recipient, 'memberId'),
                ...get(memo, '[-1].memberIdsToRemove', []),
              ]),
            );
          }
          if (
            !find(
              get(recipient, 'relationships', []),
              (relationship: Recipient) => relationship.active,
            )
          ) {
            set(recipient, 'active', false);
            set(recipient, 'inactiveReason', 'NO_ACTIVE_GUARDIAN');
          }
          set(youthList, get(recipient, 'memberId'), recipient);
        }

        return memo;
      },
      recipients,
    );

    recipients = reduce(
      recipients,
      (memo, recipient, memberId) => {
        if (
          indexOf(get(recipient, 'tags', []), personTagType.PARTICIPANT) === -1
        ) {
          set(memo, memberId, recipient);
        } else {
          set(youthList, memberId, recipient);
        }
        return memo;
      },
      {},
    );

    // Filter invalid recipients
    recipients = filter(
      recipients,
      (recipient: { isInvalid: boolean }) => !recipient.isInvalid,
    );

    return {
      adults: sortRecipients(map(recipients, recipient => recipient)),
      youth: sortRecipients(map(youthList, recipient => recipient)),
      subunits: getSubunits(
        concat(
          map(recipients, recipient => recipient),
          map(youthList, recipient => recipient),
        ),
      ),
    };
  } catch {
    return {
      adults: [],
      youth: [],
      subunits: [],
    };
  }
};
